<section class="about-admin">

  <!-- =====================================================
       PAGE HEADER
       ===================================================== -->

  <header class="section-hero">
    <div class="section-hero__content">
      <div class="section-hero__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path
            d="M4 19.5V6.8c0-.7.4-1.3 1-1.6 2-.8 4-.8 7 .7 3-1.5 5-1.5 7-.7.6.3 1 .9 1 1.6v12.7c0 .4-.4.7-.8.5-1.8-.7-3.7-.6-6.2.7-.6.3-1.4.3-2 0-2.5-1.3-4.4-1.4-6.2-.7-.4.2-.8-.1-.8-.5Z"
          />
          <path d="M12 6v13.8" />
        </svg>
      </div>

      <div>
        <span class="section-hero__eyebrow">
          Page Content
        </span>

        <h1>About Page</h1>

        <p>
          Manage the About page hero, story sections, imagery,
          visibility, and display order from one place.
        </p>
      </div>
    </div>

    <div class="section-hero__actions">
      <button
        type="button"
        class="button button--primary"
        (click)="openCreateForm()">
        Add Section
      </button>
    </div>
  </header>

  <!-- =====================================================
       HERO LOADING
       ===================================================== -->

  <div
    *ngIf="heroLoading"
    class="state-card">

    <div class="loading-spinner"></div>

    <div>
      <strong>Loading About hero</strong>

      <p>
        Retrieving the current page heading and background.
      </p>
    </div>
  </div>

  <!-- =====================================================
       HERO EDITOR
       ===================================================== -->

  <section
    *ngIf="!heroLoading && hero"
    class="editor-card hero-editor">

    <header class="editor-card__header">
      <div class="editor-card__heading">
        <div class="hero-editor__badge">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 18 9 9l4 5 2-3 6 7" />
            <circle cx="17.5" cy="6.5" r="2.5" />
          </svg>
        </div>

        <div>
          <span class="card-eyebrow">
            Page Hero
          </span>

          <h2>About Hero</h2>

          <p>
            Controls the heading and background shown
            at the top of the About page.
          </p>
        </div>
      </div>

      <span class="status-pill status-pill--active">
        Hero
      </span>
    </header>

    <div class="editor-layout hero-editor__layout">

      <!-- HERO TEXT -->

      <div class="editor-fields">

        <div class="field-group">
          <label for="about-hero-eyebrow">
            Eyebrow Text
          </label>

          <input
            id="about-hero-eyebrow"
            type="text"
            [(ngModel)]="heroDraft.eyebrowText"
            placeholder="Who We Are">
        </div>

        <div class="hero-title-grid">

          <div class="field-group">
            <label for="about-hero-title">
              Title
            </label>

            <input
              id="about-hero-title"
              type="text"
              [(ngModel)]="heroDraft.title"
              placeholder="Built Around">
          </div>

          <div class="field-group">
            <label for="about-hero-accent">
              Accent Text
            </label>

            <input
              id="about-hero-accent"
              type="text"
              [(ngModel)]="heroDraft.accentText"
              placeholder="Community">
          </div>

        </div>

        <div class="field-group">
          <label for="about-hero-subtitle">
            Subtitle
          </label>

          <textarea
            id="about-hero-subtitle"
            rows="5"
            [(ngModel)]="heroDraft.subtitle"
            placeholder="Add a short introduction to the About page.">
          </textarea>
        </div>

        <div class="hero-copy-preview">
          <span *ngIf="heroDraft.eyebrowText">
            {{ heroDraft.eyebrowText }}
          </span>

          <strong>
            {{ heroDraft.title || 'Hero title' }}

            <em *ngIf="heroDraft.accentText">
              {{ heroDraft.accentText }}
            </em>
          </strong>

          <p *ngIf="heroDraft.subtitle">
            {{ heroDraft.subtitle }}
          </p>
        </div>

      </div>

      <!-- HERO IMAGE -->

      <aside class="media-editor">
        <div class="media-editor__header">
          <div>
            <span class="card-eyebrow">
              Hero Background
            </span>

            <h3>Background Image</h3>
          </div>
        </div>

        <div
          class="image-preview hero-image-preview"
          [class.image-preview--empty]="!getHeroImageUrl()">

          <img
            *ngIf="getHeroImageUrl() as heroImageUrl"
            [src]="heroImageUrl"
            alt="About hero preview">

          <div
            *ngIf="!getHeroImageUrl()"
            class="image-placeholder">

            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5v-13Z"
              />
              <circle cx="9" cy="9" r="1.5" />
              <path d="m6.5 17 4-4 2.5 2.5 2-2 2.5 3.5" />
            </svg>

            <strong>No hero image</strong>

            <span>
              Select an image for the About page hero.
            </span>
          </div>
        </div>

        <div class="media-actions">
          <label class="button button--secondary file-button">
            {{
              getHeroImageUrl()
                ? 'Replace Image'
                : 'Choose Image'
            }}

            <input
              type="file"
              accept="image/*"
              (change)="onHeroImageSelected($event)">
          </label>

          <button
            *ngIf="getHeroImageUrl()"
            type="button"
            class="button button--danger-soft"
            (click)="removeHeroImage()">
            Remove
          </button>
        </div>

        <div
          *ngIf="heroImage"
          class="pending-change">
          New hero image selected. Save the hero to apply it.
        </div>
      </aside>

    </div>

    <footer class="editor-card__footer">
      <div class="hero-editor__hint">
        Changes affect the public About page hero.
      </div>

      <div class="save-actions">
        <button
          type="button"
          class="button button--ghost"
          [disabled]="!heroHasChanges() || heroSaving"
          (click)="resetHero()">
          Reset
        </button>

        <button
          type="button"
          class="button button--primary"
          [disabled]="!heroHasChanges() || heroSaving"
          (click)="saveHero()">

          <span
            *ngIf="heroSaving"
            class="button-spinner">
          </span>

          {{
            heroSaving
              ? 'Saving...'
              : 'Save Hero'
          }}
        </button>
      </div>
    </footer>
  </section>

  <!-- =====================================================
       SECTION LOADING
       ===================================================== -->

  <div
    *ngIf="loading"
    class="state-card">

    <div class="loading-spinner"></div>

    <div>
      <strong>Loading About sections</strong>

      <p>
        Retrieving the current About page content.
      </p>
    </div>
  </div>

  <ng-container *ngIf="!loading">

    <!-- ===================================================
         CREATE SECTION
         =================================================== -->

    <section
      *ngIf="showCreateForm"
      class="editor-card editor-card--new">

      <header class="editor-card__header">
        <div class="editor-card__heading">
          <div class="number-badge number-badge--new">
            +
          </div>

          <div>
            <span class="card-eyebrow">
              New Section
            </span>

            <h2>Add About Content</h2>

            <p>
              Create another content block for the public About page.
            </p>
          </div>
        </div>

        <span class="status-pill status-pill--active">
          Active
        </span>
      </header>

      <div class="editor-layout">

        <!-- CONTENT -->

        <div class="editor-fields">

          <div class="field-group">
            <label for="new-about-title">
              Section Title
            </label>

            <input
              id="new-about-title"
              type="text"
              maxlength="150"
              [(ngModel)]="newSection.title"
              placeholder="Our Story">
          </div>

          <div class="field-group">
            <div class="field-label-row">
              <label for="new-about-body">
                Section Content
              </label>

              <span>
                {{ newSection.body.length }} characters
              </span>
            </div>

            <textarea
              id="new-about-body"
              rows="9"
              [(ngModel)]="newSection.body"
              placeholder="Tell visitors about your organization, history, people, or community involvement.">
            </textarea>
          </div>

          <label class="visibility-toggle">
            <input
              type="checkbox"
              [(ngModel)]="newSection.isActive">

            <span class="visibility-toggle__switch"></span>

            <span class="visibility-toggle__content">
              <strong>
                Show this section
              </strong>

              <small>
                Active sections appear on the public About page.
              </small>
            </span>
          </label>

        </div>

        <!-- IMAGE -->

        <aside class="media-editor">
          <div class="media-editor__header">
            <div>
              <span class="card-eyebrow">
                Section Image
              </span>

              <h3>Supporting Image</h3>
            </div>
          </div>

          <div
            class="image-preview"
            [class.image-preview--empty]="!newSectionPreview">

            <img
              *ngIf="newSectionPreview"
              [src]="newSectionPreview"
              alt="New About section preview">

            <div
              *ngIf="!newSectionPreview"
              class="image-placeholder">

              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5v-13Z"
                />
                <circle cx="9" cy="9" r="1.5" />
                <path d="m6.5 17 4-4 2.5 2.5 2-2 2.5 3.5" />
              </svg>

              <strong>No image selected</strong>

              <span>
                Add an image to support this section.
              </span>
            </div>
          </div>

          <div class="media-actions">
            <label class="button button--secondary file-button">
              Choose Image

              <input
                type="file"
                accept="image/*"
                (change)="onNewImageSelected($event)">
            </label>

            <button
              *ngIf="newSectionPreview"
              type="button"
              class="button button--danger-soft"
              (click)="removeNewImage()">
              Remove
            </button>
          </div>
        </aside>

      </div>

      <footer class="editor-card__footer">
        <button
          type="button"
          class="button button--ghost"
          [disabled]="creating"
          (click)="cancelCreate()">
          Cancel
        </button>

        <button
          type="button"
          class="button button--primary"
          [disabled]="creating"
          (click)="createSection()">

          <span
            *ngIf="creating"
            class="button-spinner">
          </span>

          {{
            creating
              ? 'Creating...'
              : 'Create Section'
          }}
        </button>
      </footer>
    </section>

    <!-- ===================================================
         EMPTY STATE
         =================================================== -->

    <div
      *ngIf="sections.length === 0 && !showCreateForm"
      class="empty-state">

      <div class="empty-state__icon">
        <svg viewBox="0 0 24 24">
          <path
            d="M4 19.5V6.8c0-.7.4-1.3 1-1.6 2-.8 4-.8 7 .7 3-1.5 5-1.5 7-.7.6.3 1 .9 1 1.6v12.7c0 .4-.4.7-.8.5-1.8-.7-3.7-.6-6.2.7-.6.3-1.4.3-2 0-2.5-1.3-4.4-1.4-6.2-.7-.4.2-.8-.1-.8-.5Z"
          />
        </svg>
      </div>

      <h2>No About sections yet</h2>

      <p>
        Create your first section to begin building the About page.
      </p>

      <button
        type="button"
        class="button button--primary"
        (click)="openCreateForm()">
        Add First Section
      </button>
    </div>

    <!-- ===================================================
         EXISTING SECTIONS
         =================================================== -->

    <div
      *ngIf="sections.length > 0"
      class="sections-list">

      <div class="sections-summary">
        <div>
          <strong>
            {{ sections.length }}
            {{
              sections.length === 1
                ? 'section'
                : 'sections'
            }}
          </strong>

          <span>
            Sections are displayed from top to bottom in this order.
          </span>
        </div>
      </div>

      <article
        *ngFor="
          let section of sections;
          let index = index;
          trackBy: trackBySectionId
        "
        class="editor-card"
        [class.editor-card--hidden]="!getDraft(section.id).isActive">

        <header class="editor-card__header">
          <div class="editor-card__heading">
            <div class="number-badge">
              {{ index + 1 }}
            </div>

            <div>
              <span class="card-eyebrow">
                About Section
              </span>

              <h2>
                {{
                  getDraft(section.id).title ||
                  'Untitled Section'
                }}
              </h2>

              <p>
                Section {{ index + 1 }} of {{ sections.length }}
              </p>
            </div>
          </div>

          <div class="header-controls">
            <div class="order-controls">
              <button
                type="button"
                class="icon-button"
                aria-label="Move section up"
                title="Move up"
                [disabled]="index === 0"
                (click)="moveUp(index)">
                ↑
              </button>

              <button
                type="button"
                class="icon-button"
                aria-label="Move section down"
                title="Move down"
                [disabled]="index === sections.length - 1"
                (click)="moveDown(index)">
                ↓
              </button>
            </div>

            <span
              class="status-pill"
              [class.status-pill--active]="getDraft(section.id).isActive"
              [class.status-pill--inactive]="!getDraft(section.id).isActive">

              {{
                getDraft(section.id).isActive
                  ? 'Active'
                  : 'Hidden'
              }}
            </span>
          </div>
        </header>

        <div class="editor-layout">

          <!-- CONTENT -->

          <div class="editor-fields">

            <div class="field-group">
              <label [for]="'about-title-' + section.id">
                Section Title
              </label>

              <input
                [id]="'about-title-' + section.id"
                type="text"
                maxlength="150"
                [(ngModel)]="getDraft(section.id).title">
            </div>

            <div class="field-group">
              <div class="field-label-row">
                <label [for]="'about-body-' + section.id">
                  Section Content
                </label>

                <span>
                  {{ getDraft(section.id).body.length }} characters
                </span>
              </div>

              <textarea
                [id]="'about-body-' + section.id"
                rows="9"
                [(ngModel)]="getDraft(section.id).body">
              </textarea>
            </div>

            <label class="visibility-toggle">
              <input
                type="checkbox"
                [(ngModel)]="getDraft(section.id).isActive">

              <span class="visibility-toggle__switch"></span>

              <span class="visibility-toggle__content">
                <strong>
                  Show this section
                </strong>

                <small>
                  Hidden sections remain saved but
                  are not shown publicly.
                </small>
              </span>
            </label>

          </div>

          <!-- MEDIA -->

          <aside class="media-editor">
            <div class="media-editor__header">
              <div>
                <span class="card-eyebrow">
                  Section Image
                </span>

                <h3>Supporting Image</h3>
              </div>
            </div>

            <div
              class="image-preview"
              [class.image-preview--empty]="!getImageUrl(section)">

              <img
                *ngIf="getImageUrl(section) as imageUrl"
                [src]="imageUrl"
                [alt]="getDraft(section.id).title">

              <div
                *ngIf="!getImageUrl(section)"
                class="image-placeholder">

                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5v-13Z"
                  />
                  <circle cx="9" cy="9" r="1.5" />
                  <path d="m6.5 17 4-4 2.5 2.5 2-2 2.5 3.5" />
                </svg>

                <strong>No image assigned</strong>

                <span>
                  This section will display without an image.
                </span>
              </div>
            </div>

            <div class="media-actions">
              <label class="button button--secondary file-button">
                {{
                  getImageUrl(section)
                    ? 'Replace Image'
                    : 'Choose Image'
                }}

                <input
                  type="file"
                  accept="image/*"
                  (change)="onImageSelected(section.id, $event)">
              </label>

              <button
                *ngIf="getImageUrl(section)"
                type="button"
                class="button button--danger-soft"
                (click)="removeImage(section.id)">
                Remove
              </button>
            </div>

            <div
              *ngIf="selectedImages.has(section.id)"
              class="pending-change">

              New image selected.
              Save this section to apply it.
            </div>
          </aside>

        </div>

        <footer class="editor-card__footer">
          @if (pendingDeleteSectionId === section.id) {
            <div class="section-delete-confirmation" role="group" [attr.aria-labelledby]="'delete-warning-' + section.id" (keydown.escape)="cancelDeleteSection()">
              <p [id]="'delete-warning-' + section.id" role="status">Delete this section? This cannot be undone.</p>
              <div class="section-delete-actions">
                <button type="button" class="button button--ghost" [disabled]="isDeleting(section.id)" (click)="cancelDeleteSection()">Cancel</button>
                <button type="button" class="button button--danger-soft" [disabled]="isDeleting(section.id)" (click)="deleteSection(section)">{{ isDeleting(section.id) ? 'Deleting...' : 'Delete Section' }}</button>
              </div>
            </div>
          } @else {
          <button
            type="button"
            class="button button--danger-soft"
            [disabled]="isDeleting(section.id)"
            (click)="requestDeleteSection(section)">

            {{
              isDeleting(section.id)
                ? 'Deleting...'
                : 'Delete Section'
            }}
          </button>
          }

          <div class="save-actions">
            <button
              type="button"
              class="button button--ghost"
              [disabled]="
                !hasChanges(section) ||
                isSaving(section.id)
              "
              (click)="resetSection(section)">
              Reset
            </button>

            <button
              type="button"
              class="button button--primary"
              [disabled]="
                !hasChanges(section) ||
                isSaving(section.id)
              "
              (click)="saveSection(section)">

              <span
                *ngIf="isSaving(section.id)"
                class="button-spinner">
              </span>

              {{
                isSaving(section.id)
                  ? 'Saving...'
                  : 'Save Changes'
              }}
            </button>
          </div>
        </footer>

      </article>

    </div>

  </ng-container>

</section>
